import React, { useState, useMemo, useEffect } from "react";
import { Search, Loader2, Bell } from "lucide-react";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { UserAvatar } from "@/modern/components/user-avatar";
import { cn } from "@/modern/utils";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { useDebounce } from "@/react/hooks/useDebounce";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { triggerNudgeAlert } from "@/other/utils/otherUtils";
import { toast } from "sonner";

interface NudgeUsersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklist: Checklist;
  onUpdateChecklist: (checklist: Checklist, updates: Partial<Checklist>) => boolean;
}

interface BoardUser {
  id: number;
  name: string;
  profileImageURL?: string;
  email?: string;
  archived: boolean;
  isDeactivated?: boolean;
}

const USERS_PER_PAGE = 20;

export const NudgeUsersModal: React.FC<NudgeUsersModalProps> = ({ open, onOpenChange, checklist, onUpdateChecklist }) => {
  const { project } = useSwivuBryntumContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [displayedCount, setDisplayedCount] = useState(USERS_PER_PAGE);
  const [isSaving, setIsSaving] = useState(false);

  // Debounce search term to avoid performance issues with large datasets
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Initialize selected users from checklist's assignedBoardUsers
  useEffect(() => {
    if (open && checklist) {
      setSelectedUserIds([...(checklist.assignedBoardUsers || [])]);
    }
  }, [open, checklist]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setDisplayedCount(USERS_PER_PAGE);
    }
  }, [open]);

  // Get all board users from the store
  const allBoardUsers = useMemo(() => {
    if (!project?.boardUsersStore) return [];

    const users: BoardUser[] = [];
    project.boardUsersStore.forEach((user: any) => {
      if (!user.archived && !user.isDeactivated) {
        users.push({
          id: user.id,
          name: user.name,
          profileImageURL: user.profileImageURL,
          email: user.email,
          archived: user.archived
        });
      }
    });

    return users;
  }, [project]);

  // Filter users based on debounced search term
  const filteredUsers = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return allBoardUsers;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return allBoardUsers.filter((user) => user.name?.toLowerCase().includes(searchLower) || user.email?.toLowerCase().includes(searchLower));
  }, [allBoardUsers, debouncedSearchTerm]);

  // Determine which users to display
  const usersToDisplay = useMemo(() => {
    // If searching, show all filtered results
    if (debouncedSearchTerm.trim()) {
      return filteredUsers;
    }
    // If not searching, show paginated results
    return allBoardUsers.slice(0, displayedCount);
  }, [debouncedSearchTerm, filteredUsers, allBoardUsers, displayedCount]);

  // Reset displayed count when search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      setDisplayedCount(USERS_PER_PAGE);
    }
  }, [debouncedSearchTerm]);

  // Infinite scroll: load more users
  const loadMoreUsers = () => {
    setDisplayedCount((prev) => prev + USERS_PER_PAGE);
  };

  // Check if there are more users to load (only relevant when not searching)
  const hasMoreUsers = !debouncedSearchTerm.trim() && displayedCount < allBoardUsers.length;

  // Check if we're currently searching (for loading state)
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Toggle user selection
  const toggleSelection = (userId: number, checked: boolean) => {
    setSelectedUserIds((prev) => {
      if (checked) {
        return [...prev, userId];
      } else {
        return prev.filter((id) => id !== userId);
      }
    });
  };

  // Handle save action
  const handleSave = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("No users selected", {
        description: "Please select at least one user to nudge"
      });
      return;
    }

    setIsSaving(true);
    try {
      // Update the checklist's assignedBoardUsers
      const success = onUpdateChecklist(checklist, {
        assignedBoardUsers: selectedUserIds
      });

      if (success) {
        // Sync changes to the server
        await project.sync();

        // Trigger the nudge alert
        await triggerNudgeAlert(checklist.id, project, null);

        toast.success("Nudge Sent", {
          description: `Nudge sent to ${selectedUserIds.length} user(s)`
        });

        onOpenChange(false);
      }
    } catch (error) {
      console.error("Failed to nudge users:", error);
      toast.error("Failed to send nudge", {
        description: "Something went wrong. Please try again."
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 flex flex-col">
        <DialogHeader className="px-6 py-5">
          <DialogTitle className="flex items-center gap-2 font-normal">
            <Bell className="size-4" />
            Nudge Users
          </DialogTitle>
          <DialogDescription className="pt-1.5">Select users to send a nudge notification for this checklist</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col px-6 pb-5">
          {/* Search Input */}
          <div className="relative py-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background border-input focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* User List */}
          <ScrollArea className="h-[400px] mb-4">
            {usersToDisplay.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                {isSearching ? (
                  <>
                    <Loader2 className="h-10 w-10 text-muted-foreground/50 mb-3 animate-spin" />
                    <p className="text-sm text-muted-foreground">Searching users...</p>
                  </>
                ) : (
                  <>
                    <Search className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">{debouncedSearchTerm ? "No users found matching your search" : "No available users"}</p>
                    {debouncedSearchTerm && <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>}
                  </>
                )}
              </div>
            ) : (
              <InfiniteScroll hasMore={hasMoreUsers} isLoading={false} next={loadMoreUsers} threshold={0.8}>
                {usersToDisplay.map((user, index) => {
                  const isSelected = selectedUserIds.includes(user.id);

                  return (
                    <div
                      key={user.id}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors rounded-md",
                        "hover:bg-accent",
                        isSelected && "bg-accent",
                        index < usersToDisplay.length - 1 && "mb-1"
                      )}
                      onClick={() => toggleSelection(user.id, !isSelected)}
                    >
                      <UserAvatar
                        user={{
                          id: user.id,
                          name: user.name || "N/A",
                          profileImageURL: user.profileImageURL,
                          archived: user.archived
                        }}
                        size="default"
                        showName={true}
                        secondaryText={user.email}
                        className="max-w-[350px]"
                      />

                      {/* Checkbox */}
                      <Checkbox checked={isSelected} onCheckedChange={(checked) => toggleSelection(user.id, checked as boolean)} onClick={(e) => e.stopPropagation()} className="flex-shrink-0" />
                    </div>
                  );
                })}
                {hasMoreUsers && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </InfiniteScroll>
            )}
          </ScrollArea>

          {/* Selection Counter & Action Buttons */}
          <div className="flex items-center justify-between gap-3 pt-5 border-t">
            <span className={cn("text-sm", selectedUserIds.length > 0 ? "text-foreground font-medium" : "text-muted-foreground")}>{selectedUserIds.length} selected</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} size="sm" disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} size="sm" disabled={selectedUserIds.length === 0 || isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Nudge"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
